import { Injectable } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { Observable } from "rxjs";
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class MapService {

    private map;

    constructor() {
        // @ts-ignore
        mapboxgl.accessToken = 'pk.eyJ1IjoiY3VsdHVyZWFwcCIsImEiOiJja2dwbGY0ZHowYjdmMnd0ZDQ0ZWRyZDY4In0.3euKoCD40AHQsnmUIWg32Q';
    }

    clusterMap() {
        this.map = new mapboxgl.Map({
            container: 'map',
            style: 'mapbox://styles/mapbox/outdoors-v10',
            center: [23.729745, 39.085262],
            zoom: 6
        });
        return this.map;
    }

    simpleMap(options): Observable<any> {
        return new Observable<any>(
            (observer) => {
                let map;
                map = new mapboxgl.Map({
                    container: options.container,
                    style: options.mapStyle,
                    center: options.center,
                    zoom: options.zoom
                });
                map.on('load', () => {
                    observer.next(map);
                });
            })
    }

    addMarker(markerOptions): Observable<any> {
        return new Observable<any>(
            (observer) => {
                let marker = null;
                if (markerOptions.marker) {
                    let options: mapboxgl.MarkerOptions = {
                        offset: markerOptions.offset
                    };
                    let element: HTMLElement = markerOptions.element
                    if (element) {
                        marker = new mapboxgl.Marker(element, options)
                    } else {
                        marker = new mapboxgl.Marker(options)
                    }
                    marker
                        .setLngLat(markerOptions.marker)
                        .addTo(markerOptions.map);
                }
                observer.next(marker);
            })
    }


    addGeoJsonLayer(name, geoJson, subject: Subject<any>, popup) {
        this.map.addSource(name, {
            type: 'geojson',
            data: geoJson,
            cluster: true,
            clusterMaxZoom: 14, // Max zoom to cluster points on
            clusterRadius: 20 // Radius of each cluster when clustering points (defaults to 50)
        });
        this.map.addLayer({
            id: 'cluster-' + name,
            type: 'circle',
            source: name,
            layout: {
                'visibility': 'none'
            },
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#2b82cb',
                'circle-radius': 20,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff'
            }
        });
        this.map.addLayer({
            id: 'cluster-count-' + name,
            type: 'symbol',
            source: name,
            filter: ['has', 'point_count'],
            layout: {
                'visibility': 'none',
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
            }
        });
        this.map.addLayer({
            id: "unclustered-points-" + name,
            type: "symbol",
            source: name,
            filter: ["all",
                ["!has", "point_count"]
            ],
            layout: {
                'visibility': 'none',
                "icon-image": "pin",
                "icon-size": 0.8
            }
        });

        this.map.addLayer({
            id: "unclustered-points-favorite-" + name,
            type: "symbol",
            source: name,
            filter: ["all",
                ["!has", "point_count"]
                ,["==", "isFavorite", true]
            ],
            layout: {
                'visibility': 'none',
                "icon-image": "pin-favorite",
                "icon-size": 0.8
            }
        });

        // inspect a cluster on click
        this.map.on('click', 'cluster-' + name, (e) => {
            var features = this.map.queryRenderedFeatures(e.point, {
                layers: ['cluster-' + name]
            });
            var clusterId = features[0].properties.cluster_id;
            this.map.getSource(name).getClusterExpansionZoom(
                clusterId,
                (err, zoom) => {
                    if (err) return;

                    this.map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom
                    });
                }
            );
        });
        this.map.on('click', "unclustered-points-" + name, clickCallback.bind(this));
        this.map.on('click', "unclustered-points-favorite-" + name, clickCallback.bind(this));

        function clickCallback(e) {
            const coordinates = e.lngLat;

            // Ensure that if the map is zoomed out such that
            // multiple copies of the feature are visible, the
            // popup appears over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            subject.next(e.features[0].properties);
            setTimeout(() => {
                popup
                    .setLngLat(coordinates)
                    .addTo(this.map);
            })
        }
    }

    activateClusterLayer(map, name: string, otherlayers?: string[]) {
        for (let layerId of otherlayers) {
            const layer = map.getLayer("cluster-" + layerId);
            if (layer) {
                map.setLayoutProperty("cluster-" + layerId, 'visibility', 'none');
                map.setLayoutProperty("unclustered-points-" + layerId, 'visibility', 'none');
                map.setLayoutProperty("unclustered-points-favorite-" + layerId, 'visibility', 'none');
                map.setLayoutProperty("cluster-count-" + layerId, 'visibility', 'none');
            }
        }
        const currentLayer = map.getLayer("cluster-" + name);
        if (currentLayer) {
            map.setLayoutProperty("cluster-" + name, 'visibility', 'visible');
            map.setLayoutProperty("unclustered-points-" + name, 'visibility', 'visible');
            map.setLayoutProperty("unclustered-points-favorite-" + name, 'visibility', 'visible');
            map.setLayoutProperty("cluster-count-" + name, 'visibility', 'visible');
        }
    }

    mapLoadImage(map) {
        map.loadImage(
            'assets/images/marker-icon.png',
            (error, image) => {
                if (error) throw error;
                map.addImage('pin', image);
            })
        map.loadImage(
            'assets/images/marker-icon-favorite.png',
            (error, image) => {
                if (error) throw error;
                map.addImage('pin-favorite', image);
            })
    }
}
